import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Users, CalendarDays, Clock, MapPin, PencilLine, FileCheck2 } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHead from "../components/SectionHead";
import Reveal from "../components/Reveal";
import { MEMBERS, MINUTES } from "../data/site";

function MemberCard({ m, i }) {
    return (
        <Reveal delay={i * 0.08} className="bg-ink">
            <div className="border border-white/10 h-full group hover:border-amber/50 transition-colors" data-testid={`member-card-${m.name.toLowerCase()}`}>
                <div className="relative aspect-[4/3] bg-module overflow-hidden border-b border-white/10 flex items-center justify-center">
                    <div className="absolute inset-0 scanlines" />
                    <div className="text-center">
                        <p className="font-display text-7xl text-stroke-amber font-semibold">{m.initials}</p>
                        <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-ash border border-white/15 px-2 py-1 inline-block bg-ink/60">
                            Photo withheld — pending consent
                        </p>
                    </div>
                </div>
                <div className="p-6 sm:p-7">
                    <div className="flex items-center justify-between gap-3 mb-3">
                        <h3 className="font-display text-2xl text-paper">{m.name}</h3>
                        <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-amber border border-amber/40 px-2 py-1">
                            {m.role}
                        </span>
                    </div>
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-ash mb-4">{m.responsibilities}</p>
                    <p className="text-sm leading-relaxed text-ash">{m.bio}</p>
                </div>
            </div>
        </Reveal>
    );
}

function MinuteItem({ minute, i }) {
    const [open, setOpen] = useState(i === 0);
    return (
        <div className="border border-white/10 bg-ink" data-testid={`meeting-minutes-accordion-item-${i + 1}`}>
            <button
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                data-testid={`meeting-minutes-toggle-button-${i + 1}`}
                className="w-full flex items-center justify-between gap-4 p-6 text-left hover:bg-sheet transition-colors"
            >
                <div className="flex items-baseline gap-5">
                    <span className="font-mono text-xs text-amber">{minute.no}</span>
                    <span className="font-display text-lg sm:text-2xl text-paper">{minute.date}</span>
                </div>
                <ChevronDown
                    size={18}
                    className={`text-amber shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
            </button>
            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="border-t border-white/10 p-6 sm:p-8 grid lg:grid-cols-[220px_1fr] gap-8">
                            <div className="space-y-5">
                                <div>
                                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber mb-2">
                                        <Clock size={12} /> Time & place
                                    </p>
                                    <p className="text-sm text-ash leading-relaxed">
                                        {minute.time}
                                        <br />
                                        {minute.place}
                                    </p>
                                </div>
                                <div>
                                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber mb-2">
                                        <Users size={12} /> Present
                                    </p>
                                    <p className="text-sm text-ash">{minute.present.join(", ")}</p>
                                    {minute.apologies.length > 0 && (
                                        <p className="text-xs text-ash/70 mt-2">
                                            Apologies: {minute.apologies.join("; ")}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="space-y-6">
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber mb-3">Discussed</p>
                                    <ul className="space-y-2">
                                        {minute.items.map((item, k) => (
                                            <li key={k} className="text-sm leading-relaxed text-ash flex gap-3">
                                                <span className="text-ash/50">—</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber mb-3">Decisions</p>
                                    <ul className="space-y-2">
                                        {minute.decisions.map((d, k) => (
                                            <li key={k} className="text-sm leading-relaxed text-paper/85 flex gap-3">
                                                <FileCheck2 size={14} className="text-match shrink-0 mt-0.5" />
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber mb-3">Actions</p>
                                    <ul className="space-y-2">
                                        {minute.actions.map((a, k) => (
                                            <li key={k} className="text-sm leading-relaxed text-ash flex gap-3">
                                                <PencilLine size={14} className="text-amber shrink-0 mt-0.5" />
                                                <span>
                                                    {a.task} <span className="font-mono text-amber">→ {a.owner}</span>
                                                  </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default function GroupProcess() {
    return (
        <div>
            <PageHero
                fileNo="FR-NZ-09"
                lines={["Group process", <>& <i className="text-amber">minutes</i></>]}
                lede="How this dossier was made: who did what, how we met, and the record of every working session. Our process ran on weekly meetings, a shared source library, and a simple rule — every claim on the site must survive Moelili'a's citation check."
                meta={["SUBJECT: THE TEAM AT WORK"]}
            />

            <section className="border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="9.1"
                        kicker="The team"
                        title="Roles & responsibilities"
                        lede="Three members, three complementary roles — as allocated at our first meeting and carried through to submission."
                    />
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {MEMBERS.map((m, i) => (
                            <MemberCard key={m.name} m={m} i={i} />
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-sheet border-b border-white/10">
                <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
                    <SectionHead
                        no="9.2"
                        kicker="Documentation"
                        title="Meeting minutes"
                        lede="Minutes of our working sessions, recorded by the Team Leader and approved at the following meeting."
                    />
                    <Reveal className="mb-8">
                        <div className="flex flex-wrap items-center gap-6 font-mono text-[11px] text-ash border border-white/10 px-5 py-4">
                            <span className="flex items-center gap-2">
                                <CalendarDays size={13} className="text-amber" /> Weekly · Mondays
                            </span>
                            <span className="flex items-center gap-2">
                                <MapPin size={13} className="text-amber" /> On campus & online
                            </span>
                            <span>Chair: Nico · Minutes: rotating</span>
                        </div>
                    </Reveal>
                    <div className="space-y-4">
                        {MINUTES.map((minute, i) => (
                            <MinuteItem key={minute.no} minute={minute} i={i} />
                        ))}
                    </div>
                    <Reveal delay={0.1} className="mt-8">
                        <p className="font-mono text-[11px] leading-relaxed text-ash/70 border-l border-ash/40 pl-4">
                            * Placeholder minutes prepared to document our working structure — replace with your
                            group's own records before submission.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
